import { Badge, Button, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * One row per integration — the same shape for all six.
 *
 * Today WhatsApp, Messenger, Instagram, Google, e-commerce and CRM each have
 * their own page, their own layout and their own vocabulary, so a vendor has to
 * learn six things to answer one question. This is that one thing.
 *
 * The identity slot carries what a human recognises — a phone number, a page
 * name, an @handle, a shop domain — never the raw id that names it internally.
 * The ids aren't deleted, they move into the Advanced disclosure inside the
 * manage drawer, where the person who needs them will look.
 */
export type ConnectionHealth = 'working' | 'attention' | 'pending' | 'disconnected';

const HEALTH_LABEL: Record<ConnectionHealth, string> = {
  working: 'Working',
  attention: 'Needs attention',
  pending: 'Pending verification',
  disconnected: 'Not connected',
};

const HEALTH_COLOR: Record<ConnectionHealth, string> = {
  working: 'green',
  attention: 'orange',
  pending: 'yellow',
  disconnected: 'gray',
};

export interface ConnectionRowProps {
  icon: ReactNode;
  name: string;
  /** What a human recognises. Falls back to a prompt when nothing is connected. */
  identity?: string | null;
  /** Business name, quality in words, or the specific problem. */
  detail?: string | null;
  health: ConnectionHealth;
  /** Shown when connected. */
  onManage?: () => void;
  /** Shown when not connected. */
  onConnect?: () => void;
  connectLabel?: string;
  busy?: boolean;
}

export function ConnectionRow({
  icon,
  name,
  identity,
  detail,
  health,
  onManage,
  onConnect,
  connectLabel = 'Connect',
  busy,
}: ConnectionRowProps) {
  const connected = health !== 'disconnected';

  return (
    <Group justify="space-between" wrap="nowrap" py="xs" gap="sm">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <ThemeIcon
          size={36}
          radius="md"
          variant="light"
          color={connected ? HEALTH_COLOR[health] : 'gray'}
        >
          {icon}
        </ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text fw={600} size="sm">
              {name}
            </Text>
            {identity && (
              <Text size="sm" c="dimmed" truncate>
                {identity}
              </Text>
            )}
          </Group>
          {detail && (
            <Text size="xs" c={health === 'attention' ? 'orange' : 'dimmed'} truncate>
              {detail}
            </Text>
          )}
        </Stack>
      </Group>

      <Group gap="sm" wrap="nowrap">
        <Badge variant="light" color={HEALTH_COLOR[health]}>
          {HEALTH_LABEL[health]}
        </Badge>
        {connected && onManage && (
          <Button size="compact-sm" variant="subtle" onClick={onManage}>
            {health === 'attention' ? 'Fix it' : 'Manage'}
          </Button>
        )}
        {!connected && onConnect && (
          <Button size="compact-sm" variant="light" loading={busy} onClick={onConnect}>
            {connectLabel}
          </Button>
        )}
      </Group>
    </Group>
  );
}
